import { resolveId } from '@medplum/core'
import type { CarePlan, Reference } from '@medplum/fhirtypes'
import { hasCodeableConcept } from '@seen/core/codeableConcept'
import {
  GOAL_CARE_PLAN_CATEGORY,
  INTERVENTION_CARE_PLAN_CATEGORY,
} from '@seen/core/models/care-plans/constants'
import { GoalCarePlan } from '@seen/core/models/care-plans/GoalCarePlan'
import { useRead } from '#src/hooks/useRead.ts'

interface CarePlanDeepLinkResult {
  // The resolved in-app link, or null when it can't be built (missing patient
  // context, or a parent walk that hasn't resolved yet).
  href: string | null
  // True while the goal/initial care plans needed to build the link are still
  // loading — the caller renders the title as plain text until then.
  isResolving: boolean
}

// Goals and interventions are CarePlans that render inside their initial care
// plan's page, not at their own id. The intervention route is
// `/care-plans/:initialId/interventions/:interventionId`, so an intervention
// needs the initial plan two hops up (intervention → goal → initial); a goal
// needs one hop (goal → initial). Walk `partOf` with reads to resolve them.
//
// `resourceId` is the review card's own handle for this care plan, which is
// always present — `carePlan.id` is absent on create (POST) diffs whose server
// id isn't assigned yet, so the own-id URL segment reads from `resourceId`.
export function useCarePlanDeepLink(
  carePlan: CarePlan,
  resourceId: string,
  patientId: string | undefined
): CarePlanDeepLinkResult {
  const isIntervention = hasCodeableConcept(
    carePlan.category,
    INTERVENTION_CARE_PLAN_CATEGORY
  )
  const isGoal = hasCodeableConcept(carePlan.category, GOAL_CARE_PLAN_CATEGORY)

  // Intervention → its goal (partOf[0]); goals read directly against the
  // initial plan below so this read only runs for interventions.
  const goalRef = isIntervention
    ? (carePlan.partOf?.[0] as Reference<CarePlan> | undefined)
    : undefined
  const goalQuery = useRead(GoalCarePlan, goalRef)

  // The initial plan id: for an intervention it is the goal's parent, for a
  // goal it is this care plan's own parent.
  const initialId = isIntervention
    ? goalQuery.data?.parentId
    : isGoal
      ? resolveId(carePlan.partOf?.[0])
      : undefined

  if (!patientId) {
    return { href: null, isResolving: false }
  }

  if (isIntervention) {
    if (goalQuery.isLoading) {
      return { href: null, isResolving: true }
    }
    if (!initialId) {
      return { href: null, isResolving: false }
    }
    return {
      href: `/Patient/${patientId}/care-plans/${initialId}/interventions/${resourceId}`,
      isResolving: false,
    }
  }

  if (isGoal) {
    if (!initialId) {
      return { href: null, isResolving: false }
    }
    return {
      href: `/Patient/${patientId}/care-plans/${initialId}`,
      isResolving: false,
    }
  }

  // Initial (or otherwise categorized) care plan: it is its own page.
  return {
    href: `/Patient/${patientId}/care-plans/${resourceId}`,
    isResolving: false,
  }
}
